import { APP_NAME } from '@repo/shared_utils/constants'
import { createFileRoute } from '@tanstack/react-router'
import { Database, Eye, Lock, Mail, Shield, UserCheck } from 'lucide-react'
import LandingPageWrapper from '../components/landing/landing-page-wrapper'

export const Route = createFileRoute('/privacy')({
  component: RouteComponent,
})

function RouteComponent() {
  return (
    <LandingPageWrapper>
      <div className="max-w-4xl mx-auto space-y-12 py-12">
        {/* Header */}
        <div className="text-center space-y-6">
          <div className="flex justify-center">
            <div className="p-4 bg-green-50 dark:bg-green-950/20 rounded-full">
              <Shield className="h-12 w-12 text-green-600" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
            <span className="bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 bg-clip-text text-transparent">
              Privacy Policy
            </span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Your privacy is important to us. Learn how {APP_NAME} collects, uses, and protects your
            information.
          </p>
          <p className="text-sm text-muted-foreground">Last updated: Sept 24, 2025</p>
        </div>

        {/* Information Collection */}
        <div className="bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-blue-900 rounded-2xl border border-gray-200 dark:border-gray-700 p-8 shadow-lg">
          <div className="flex items-center gap-4 mb-6">
            <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
              <Database className="h-8 w-8 text-blue-600" />
            </div>
            <h2 className="text-3xl font-bold">Information We Collect</h2>
          </div>

          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold mb-3">Personal Information</h3>
              <ul className="space-y-2 text-muted-foreground ml-4">
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                  Name, email address, and contact information
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                  Business information and tax identification numbers
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                  Employee data you input for payroll processing
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-3">Financial Information</h3>
              <ul className="space-y-2 text-muted-foreground ml-4">
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                  Bank account details for payroll processing
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                  Payment information for subscription billing
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                  Salary and compensation data
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-3">Usage Information</h3>
              <ul className="space-y-2 text-muted-foreground ml-4">
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                  How you use our services and features
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                  Device information and IP addresses
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                  Log files and analytics data
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* How We Use Information */}
        <div className="bg-gradient-to-br from-purple-50 via-white to-pink-50 dark:from-gray-900 dark:via-gray-800 dark:to-purple-900 rounded-2xl border border-gray-200 dark:border-gray-700 p-8 shadow-lg">
          <div className="flex items-center gap-4 mb-6">
            <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
              <Eye className="h-8 w-8 text-purple-600" />
            </div>
            <h2 className="text-3xl font-bold">How We Use Your Information</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-xl font-semibold">Service Delivery</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 flex-shrink-0"></span>
                  Process payroll and manage employee payments
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 flex-shrink-0"></span>
                  Generate tax reports and compliance documents
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 flex-shrink-0"></span>
                  Provide customer support and assistance
                </li>
              </ul>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-semibold">Service Improvement</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 flex-shrink-0"></span>
                  Analyze usage patterns to improve features
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 flex-shrink-0"></span>
                  Develop new payroll management tools
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 flex-shrink-0"></span>
                  Ensure system security and performance
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Data Protection */}
        <div className="bg-gradient-to-br from-red-50 via-white to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-red-900 rounded-2xl border border-gray-200 dark:border-gray-700 p-8 shadow-lg">
          <div className="flex items-center gap-4 mb-6">
            <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-xl">
              <Lock className="h-8 w-8 text-red-600" />
            </div>
            <h2 className="text-3xl font-bold">Data Protection & Security</h2>
          </div>

          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold mb-3">Security Measures</h3>
              <ul className="space-y-2 text-muted-foreground ml-4">
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0"></span>
                  Encrypted database storage with regular backups
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0"></span>
                  Social Authentication using OAuth2.0 (Google)
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0"></span>
                  Regular security audits and vulnerability assessments
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-3">Access Controls</h3>
              <ul className="space-y-2 text-muted-foreground ml-4">
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0"></span>
                  Role-based access permissions for team members
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0"></span>
                  Limited employee access to personal data only
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0"></span>
                  Comprehensive audit logs for all data access
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Data Sharing */}
        <div className="bg-gradient-to-br from-green-50 via-white to-emerald-50 dark:from-gray-900 dark:via-gray-800 dark:to-green-900 rounded-2xl border border-gray-200 dark:border-gray-700 p-8 shadow-lg">
          <div className="flex items-center gap-4 mb-6">
            <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-xl">
              <UserCheck className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-3xl font-bold">Data Sharing & Third Parties</h2>
          </div>

          <div className="space-y-6">
            <div className="bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <p className="text-green-800 dark:text-green-200 font-medium">
                We do not sell, trade, or rent your personal information to third parties.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-3">Limited Sharing Scenarios</h3>
              <ul className="space-y-2 text-muted-foreground ml-4">
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-green-600 rounded-full mt-2 flex-shrink-0"></span>
                  Government agencies for tax compliance and regulatory requirements
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-green-600 rounded-full mt-2 flex-shrink-0"></span>
                  Banking partners for direct deposit processing (encrypted)
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-green-600 rounded-full mt-2 flex-shrink-0"></span>
                  Cloud infrastructure providers with strict data protection agreements
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-green-600 rounded-full mt-2 flex-shrink-0"></span>
                  Third-party service providers for analytics, authentication, and service
                  improvement (including AWS, Google, etc.)
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-green-600 rounded-full mt-2 flex-shrink-0"></span>
                  Legal authorities when required by Philippine law
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Your Rights */}
        <div className="bg-gradient-to-br from-amber-50 via-white to-yellow-50 dark:from-gray-900 dark:via-gray-800 dark:to-amber-900 rounded-2xl border border-gray-200 dark:border-gray-700 p-8 shadow-lg">
          <div className="flex items-center gap-4 mb-6">
            <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-xl">
              <UserCheck className="h-8 w-8 text-amber-600" />
            </div>
            <h2 className="text-3xl font-bold">Your Privacy Rights</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-xl font-semibold">Data Access Rights</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-amber-600 rounded-full mt-2 flex-shrink-0"></span>
                  Request access to your personal data
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-amber-600 rounded-full mt-2 flex-shrink-0"></span>
                  Correct inaccurate or incomplete information
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-amber-600 rounded-full mt-2 flex-shrink-0"></span>
                  Export your data in a portable format
                </li>
              </ul>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-semibold">Control Rights</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-amber-600 rounded-full mt-2 flex-shrink-0"></span>
                  Delete your account and associated data
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-amber-600 rounded-full mt-2 flex-shrink-0"></span>
                  Opt out of marketing communications
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-amber-600 rounded-full mt-2 flex-shrink-0"></span>
                  Restrict processing of your data
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Data Retention */}
        <div className="bg-gradient-to-br from-indigo-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-indigo-900 rounded-2xl border border-gray-200 dark:border-gray-700 p-8 shadow-lg">
          <h2 className="text-3xl font-bold mb-6">Data Retention</h2>

          <div className="space-y-4">
            <p className="text-muted-foreground leading-relaxed">
              We retain your information only as long as necessary for the purposes outlined in
              this policy:
            </p>

            <ul className="space-y-2 text-muted-foreground ml-4">
              <li className="flex items-start gap-2">
                <span className="w-2 h-2 bg-indigo-600 rounded-full mt-2 flex-shrink-0"></span>
                Account data: Until account deletion or 2 years of inactivity
              </li>
              <li className="flex items-start gap-2">
                <span className="w-2 h-2 bg-indigo-600 rounded-full mt-2 flex-shrink-0"></span>
                Payroll records: 7 years
              </li>
              <li className="flex items-start gap-2">
                <span className="w-2 h-2 bg-indigo-600 rounded-full mt-2 flex-shrink-0"></span>
                Financial records: 7 years (for tax and audit purposes)
              </li>
            </ul>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-gradient-to-r from-teal-50 to-cyan-50 dark:from-teal-950/20 dark:to-cyan-950/20 border border-teal-200 dark:border-teal-800 rounded-2xl p-8 text-center">
          <div className="flex justify-center mb-6">
            <div className="p-3 bg-teal-100 dark:bg-teal-900/30 rounded-xl">
              <Mail className="h-8 w-8 text-teal-600" />
            </div>
          </div>
          <h2 className="text-3xl font-bold mb-4">Questions About Privacy?</h2>
          <p className="text-muted-foreground mb-6 text-lg">
            If you have any questions about this Privacy Policy or how we handle your data, please
            contact us:
          </p>
          <div className="space-y-2">
            <p className="text-lg">
              <strong>Support:</strong>{' '}
              <a
                href="mailto:cubic.payroll.101@gmail.com"
                className="text-teal-600 hover:text-teal-700 font-medium"
              >
                cubic.payroll.101@gmail.com
              </a>
            </p>
          </div>
        </div>

        {/* Updates */}
        <div className="bg-gradient-to-r from-gray-50 to-slate-50 dark:from-gray-800 dark:to-slate-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-6 text-center">
          <p className="text-muted-foreground">
            We may update this Privacy Policy from time to time. We will notify you of any changes
            by posting the new Privacy Policy on this page and updating the "Last updated" date.
          </p>
        </div>
      </div>
    </LandingPageWrapper>
  )
}
